import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, Plus } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { PageHeader, Row, Section } from '../components/form';
import { Alert, Badge, Button, Card, Input } from '../components/ui';
import { api } from '../lib/api';

interface Credential {
  id: string;
  name: string;
  accessId: string;
  keyPrefix: string;
  allowedExtensions: string[];
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

interface Issued {
  id: string;
  accessId: string;
  accessKey: string;
}

/**
 * Credentials for the embeddable Web SDK.
 *
 * These are the keys a customer's own server uses to mint per-extension signatures. They are not
 * admin logins and are deliberately kept separate: a compromised key must be revocable without
 * touching anyone's ability to administer the PBX.
 */
export function SdkCredentials() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<Issued | null>(null);

  const { data: credentials = [] } = useQuery({
    queryKey: ['api-credentials'],
    queryFn: () => api.get<Credential[]>('/api/v1/api-credentials'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/api-credentials/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['api-credentials'] }),
  });

  const columns: Array<Column<Credential>> = [
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (c) => <span className="font-medium">{c.name}</span>,
    },
    {
      key: 'accessId',
      header: 'Access ID',
      render: (c) => <code className="text-xs">{c.accessId}</code>,
    },
    {
      key: 'key',
      header: 'Key',
      render: (c) => <code className="text-xs text-muted-foreground">{c.keyPrefix}…</code>,
    },
    {
      key: 'scope',
      header: 'Scope',
      render: (c) =>
        c.allowedExtensions.length === 0 ? (
          <span className="text-muted-foreground">Any extension</span>
        ) : (
          <span>{c.allowedExtensions.join(', ')}</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (c) =>
        c.revokedAt ? (
          <Badge tone="destructive">Revoked</Badge>
        ) : (
          <Badge tone="success">Active</Badge>
        ),
    },
    {
      key: 'lastUsed',
      header: 'Last used',
      sort: (a, b) => (a.lastUsedAt ?? '').localeCompare(b.lastUsedAt ?? ''),
      render: (c) => (c.lastUsedAt ? new Date(c.lastUsedAt).toLocaleString() : 'Never'),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="API credentials"
        description="One key pair, used by both the REST API and the Web SDK. The Access Key is shown once and cannot be retrieved afterwards."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus size={14} />
            New credential
          </Button>
        }
      />

      {issued && <IssuedCard issued={issued} onDismiss={() => setIssued(null)} />}

      {creating && (
        <CreateForm
          onCancel={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            setIssued(result);
            void queryClient.invalidateQueries({ queryKey: ['api-credentials'] });
          }}
        />
      )}

      <DataTable
        rows={credentials}
        columns={columns}
        rowKey={(c) => c.id}
        search={(c) => `${c.name} ${c.accessId}`}
        searchPlaceholder="Search credentials"
        onDelete={(rows) => rows.forEach((r) => revoke.mutate(r.id))}
        deleteLabel={(rows) =>
          `Revoke ${rows.length} credential(s)? Any application using them stops working immediately.`
        }
        empty="No credentials yet. Create one to use the REST API or embed a softphone in your own application."
      />

      <Section
        title="Using these with the REST API"
        description="See API Reference for the full endpoint list."
      >
        <p className="text-sm text-muted-foreground">
          Exchange the pair for a token at <code>POST /openapi/v1.0/get_token</code>, sending them
          as <code>username</code> and <code>password</code> — the names the compatibility API uses
          for the Access ID and Access Key, kept so a migrating integration does not have to change
          its request. The token lasts 30 minutes and is passed as <code>?access_token=</code>.
        </p>
      </Section>

      <Section title="How this is used" description="The three-legged flow, in order.">
        <ol className="ml-4 list-decimal space-y-2 text-sm text-muted-foreground">
          <li>
            Your server exchanges the Access ID and Key for a short-lived token at{' '}
            <code>POST /sdk/v1/token</code>. This never happens in a browser.
          </li>
          <li>
            Your server mints a single-use signature for one extension at{' '}
            <code>POST /sdk/v1/sign/create</code>.
          </li>
          <li>
            Your browser passes that signature to <code>init()</code>, and receives an HA1 digest
            rather than a SIP password.
          </li>
        </ol>
      </Section>
    </div>
  );
}

function IssuedCard({ issued, onDismiss }: { issued: Issued; onDismiss: () => void }) {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // Selectable on screen regardless.
    }
  };

  return (
    <Card className="space-y-4 border-warning/40 bg-warning/5">
      <div className="flex items-center gap-2">
        <KeyRound size={16} className="text-warning" />
        <h2 className="font-medium">Credential created</h2>
      </div>

      <Alert tone="warning">
        The access key is shown once and is not stored in a form we can read back. Copy it now.
      </Alert>

      <div className="grid gap-3 sm:grid-cols-2">
        {[
          ['Access ID', issued.accessId],
          ['Access Key', issued.accessKey],
        ].map(([label, value]) => (
          <div key={label} className="rounded-[--radius] border border-border bg-card p-3">
            <div className="mb-1 text-xs text-muted-foreground">{label}</div>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate text-xs">{value}</code>
              <button
                onClick={() => void copy(label!, value!)}
                className="shrink-0 rounded-[--radius] p-1.5 text-muted-foreground hover:bg-muted"
                aria-label={`Copy ${label}`}
              >
                {copied === label ? (
                  <Check size={14} className="text-success" />
                ) : (
                  <Copy size={14} />
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      <Button variant="secondary" onClick={onDismiss}>
        I have copied them
      </Button>
    </Card>
  );
}

function CreateForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (issued: Issued) => void;
}) {
  const [name, setName] = useState('');
  const [scope, setScope] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.post<Issued>('/api/v1/api-credentials', {
        name,
        allowedExtensions: scope
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      }),
    onSuccess: onCreated,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <Section title="New credential">
        <div className="grid gap-4 sm:grid-cols-2">
          <Row label="Name" required hint="Which application this is for.">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Support portal"
              required
            />
          </Row>

          <Row
            label="Allowed extensions"
            hint="Comma separated. Leave empty to allow any — narrower is safer."
          >
            <Input
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder="1001, 1002"
            />
          </Row>
        </div>

        {create.error && (
          <div className="mt-4">
            <Alert>{(create.error as Error).message}</Alert>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <Button type="submit" disabled={create.isPending || !name}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </Section>
    </form>
  );
}
